import { globalStyle, style } from "@vanilla-extract/css";
import { borderRadius } from "@/client/ui/design-system/styles/border-radius.css";
import { colors } from "@/client/ui/design-system/styles/colors.css";
import {
    kGlassBackground,
    kGlassShadow,
    kGlassShadowHover,
    kGlassSheen,
} from "@/client/ui/components/glass-surface";
import { boxShadow } from "@/client/ui/design-system/styles/shadows.css";
import { spacing } from "@/client/ui/design-system/styles/spacing.css";
import { h5Css, body2Css, body3Css } from "@/client/ui/design-system/styles/typography.css";
import { kCompactSelector, kNarrowViewportQuery, kWebSelector } from "@/client/utils/displayMode";
import { kCompactHeaderHeight_px } from "@/client/utils/headerMetrics";
import { kHostBottomInset } from "@/client/utils/hostInset";
import { px } from "@/client/utils/units";
import { kIconSize_px } from "@/client/ui/design-system/icons";

// ── Page container ────────────────────────────────────────────────────────────

// Tall enough on the web for the animation to breathe above the title, then
// to fade out fully before the first section begins. It leaves 120px between
// the subtitle and the first section title.
const kSiteHeaderHeight_px = 148;
// The header is content-box, so this padding adds to its height.
const kSiteHeaderPaddingTop_px = 36;
// Shorter: a phone has less room to give a masthead. It leaves 86px between
// the subtitle and the first section title.
const kPhoneSiteHeaderHeight_px = 106;
const kPhoneSiteHeaderPaddingTop_px = 24;
// Halved: at 48 the gradient reached past the header and washed over the
// first section title beneath it.
const kScrollFadeHeight_px = 24;

export const container = style({
    display: "flex",
    flexDirection: "column",
    // iOS Safari's 100vh is the height with its toolbars hidden, so while
    // they show, the page outgrows the screen and scrolls under the list.
    // 100dvh tracks the visible height; 100vh stays as the fallback.
    height: ["100vh", "100dvh"],
    boxSizing: "border-box",
    position: "relative",
});

// The home directory owns its scrolling (100vh container, scrollArea below),
// so it opts out of the body-level host inset — otherwise the two paddings
// stack, the body outgrows the viewport, and scroll chaining can drag the
// whole directory (header included) into the blank gap.
globalStyle(`body:has(${container})`, {
    paddingBottom: 0,
    // The global 100vh minimum has the same iOS overshoot as the container.
    minHeight: ["100vh", "100dvh"],
});

// ── Scrollable cards area below the header ───────────────────────────────────

export const scrollArea = style({
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    overflowX: "hidden",
    // Reaching the end of the list must not hand the scroll on to the page.
    overscrollBehaviorY: "contain",
    display: "flex",
    flexDirection: "column",
    gap: spacing.lg,
    paddingTop: spacing.md,
    // The home page scrolls here (not the body), so the host's floating-UI
    // clearance joins this container's own bottom padding.
    paddingBottom: `calc(${spacing.lg} + ${kHostBottomInset})`,
    paddingLeft: spacing.sm,
    paddingRight: spacing.sm,
    boxSizing: "border-box",
});

export const scrollFade = style({
    position: "absolute",
    // The header's real bottom edge, where the scrolling cards pass under it.
    top: px(kSiteHeaderHeight_px + kSiteHeaderPaddingTop_px),
    left: 0,
    right: 0,
    height: px(kScrollFadeHeight_px),
    background: `linear-gradient(to bottom, ${colors.gray90}, transparent)`,
    pointerEvents: "none",
    zIndex: 1,
    selectors: {
        // The compact header is a fixed-height chrome bar, so the fade can
        // track it (see headerMetrics.ts).
        [kCompactSelector]: {
            top: px(kCompactHeaderHeight_px),
        },
    },
    "@media": {
        [kNarrowViewportQuery]: {
            selectors: {
                [kWebSelector]: {
                    // The header is content-box, so its top padding adds to
                    // its height; the fade starts at its real bottom edge.
                    top: px(kPhoneSiteHeaderHeight_px + kPhoneSiteHeaderPaddingTop_px),
                },
            },
        },
    },
});

// ── Site header ───────────────────────────────────────────────────────────────

export const siteHeader = style({
    flexShrink: 0,
    height: px(kSiteHeaderHeight_px),
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: spacing.sm,
    // Centres the 28px title on the line the app headers, the menu trigger
    // and the Sounds dock's logo share (see sounds-browser.css.ts).
    paddingTop: px(kSiteHeaderPaddingTop_px),
    // Flush with the section titles below: the scroll area's gutter plus the
    // section's own inset.
    paddingLeft: `calc(${spacing.sm} + ${spacing.xs})`,
    paddingRight: spacing.sm,
    position: "relative",
    overflow: "hidden",
    zIndex: 0,
    selectors: {
        "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            // Holds off through the top third, then eases into the page
            // colour so the animation has no visible bottom edge.
            background: `linear-gradient(to bottom, transparent 0%, transparent 25%, ${colors.gray90}B3 62%, ${colors.gray90} 88%)`,
            pointerEvents: "none",
            zIndex: 0,
        },
        [kCompactSelector]: {
            // Balanced chrome bar in the embedded panel: a fixed height with
            // the title vertically centered, matching the floating menu
            // trigger (see headerMetrics.ts).
            height: px(kCompactHeaderHeight_px),
            alignItems: "center",
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: spacing.sm,
        },
    },
    "@media": {
        [kNarrowViewportQuery]: {
            selectors: {
                [kWebSelector]: {
                    height: px(kPhoneSiteHeaderHeight_px),
                    paddingTop: px(kPhoneSiteHeaderPaddingTop_px),
                },
            },
        },
    },
});

export const headerBgHost = style({
    position: "absolute",
    inset: 0,
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: 0,
});

export const siteHeaderLeft = style({
    display: "flex",
    flexDirection: "column",
    gap: spacing.xs,
    position: "relative",
    zIndex: 1,
});

export const siteTitle = style({
    fontFamily: "Inter, sans-serif",
    fontWeight: 700,
    fontSize: px(28),
    lineHeight: "1",
    color: colors.white,
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
    selectors: {
        [kCompactSelector]: {
            fontSize: px(20),
        },
    },
    "@media": {
        [kNarrowViewportQuery]: {
            selectors: {
                [kWebSelector]: {
                    fontSize: px(20),
                },
            },
        },
    },
});

export const siteTitleText = style({
    display: "inline-block",
});

export const siteTagline = style({
    ...body2Css,
    color: colors.gray40,
    margin: 0,
    selectors: {
        [kCompactSelector]: {
            display: "none",
        },
    },
    "@media": {
        [kNarrowViewportQuery]: {
            selectors: {
                [kWebSelector]: {
                    fontSize: px(14),
                },
            },
        },
    },
});

// The glass finish (see glass-surface.ts) over a tile half a step above the
// page.
const heroCardBase = {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "space-between",
    padding: spacing.sm,
    // A little tighter than CP's xl card corner, to suit the glass edge.
    borderRadius: px(12),
    backgroundColor: kGlassBackground,
    backgroundImage: kGlassSheen,
    boxShadow: kGlassShadow,
    textDecoration: "none",
    color: "inherit",
    minHeight: px(66),
    flex: 1,
    minWidth: 0,
    gap: spacing.sm,
    transition: "background-color 150ms ease, box-shadow 150ms ease",
    ":hover": {
        backgroundColor: colors.gray80,
        boxShadow: kGlassShadowHover,
    },
};

// ── Divider ───────────────────────────────────────────────────────────────────

export const divider = style({
    border: "none",
    borderTopWidth: px(1),
    borderTopStyle: "solid",
    borderTopColor: colors.gray80,
    margin: 0,
});

// ── Section layout ────────────────────────────────────────────────────────────

export const section = style({
    display: "flex",
    flexDirection: "column",
    gap: spacing.sm,
    paddingLeft: spacing.xs,
    paddingRight: spacing.xs,
    selectors: {
        // Clear of the header's fade. Only the first section needs it; the
        // later ones already have the scroll area's gap above them.
        "&:first-child": {
            marginTop: spacing.xs,
        },
    },
});

// ── Hero section ──────────────────────────────────────────────────────────────

export const heroLabelRow = style({
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
    flexWrap: "wrap",
});

export const heroSectionTitle = style({
    ...h5Css,
    fontWeight: 300,
    margin: 0,
    color: colors.white,
});

export const previewBadge = style({
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: colors.yellow,
    color: colors.gray90,
    borderRadius: borderRadius.sm,
    padding: `${px(2)} ${px(6)}`,
    fontFamily: "Inter, sans-serif",
    fontWeight: 700,
    fontSize: px(8),
    lineHeight: px(12),
    letterSpacing: px(0.5),
    textTransform: "uppercase",
    flexShrink: 0,
});

export const heroGrid = style({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
    gap: spacing.sm,
    listStyle: "none",
    padding: 0,
    margin: 0,
});

// A flex box rather than `display: contents`: WebKit (the macOS plugin
// webview) sizes a grid row of `contents` items a little short on first
// layout, and the card's last line, the R&D tag, spills past its bottom edge.
// The card fills the item, so a row is still as tall as its tallest card.
export const heroListItem = style({
    display: "flex",
    minWidth: 0,
});

// Half a step lighter than the page (gray90). That lift, with the glass rim on
// the base, is what separates a card from the page behind it.
const heroCardNeutral = style(heroCardBase);
export const heroCard0 = heroCardNeutral;
export const heroCard1 = heroCardNeutral;
export const heroCard2 = heroCardNeutral;
export const heroCard3 = heroCardNeutral;
export const heroCard4 = heroCardNeutral;
export const heroCard = heroCardNeutral;

export const heroCardTop = style({
    display: "flex",
    flexDirection: "column",
    gap: spacing.xxs,
});

export const heroCardTitleRow = style({
    display: "flex",
    alignItems: "center",
    gap: spacing.xs,
    flexWrap: "wrap",
});

export const heroCardTitle = style({
    ...h5Css,
    fontWeight: 500,
    color: colors.white,
    margin: 0,
});

export const heroCardBy = style({
    ...body3Css,
    color: colors.gray50,
    margin: 0,
});

/**
 * What the app does, under its name.
 *
 * Not clamped. A card wide enough for two words of title is not wide enough
 * for a sentence, so these run to several lines and the row stretches to its
 * tallest card — which is the honest picture of how long the descriptions
 * currently are.
 */
export const heroCardDescription = style({
    ...body3Css,
    fontWeight: 300,
    color: colors.gray30,
    margin: 0,
});

// Pinned to the card's lower right corner. The card grows to fit a long
// description rather than letting the tag spill out.
export const heroCardBottom = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginTop: "auto",
});

// ── Section title ─────────────────────────────────────────────────────────────

export const sectionTitle = style({
    // A step above h5 for the size; regular weight, because the size is doing
    // the work and these label the grid rather than compete with the cards.
    ...h5Css,
    fontSize: px(18),
    lineHeight: px(24),
    fontWeight: 400,
    color: colors.gray40,
    margin: 0,
});

// ── Apps tabs ─────────────────────────────────────────────────────────────────

// One row of CP pills. It scrolls sideways rather than wrapping, so a narrow
// panel or phone keeps the grid where it is instead of pushing it down.
export const appsTabs = style({
    display: "flex",
    gap: spacing.xs,
    overflowX: "auto",
    scrollbarWidth: "none",
    // Room for the focus ring, which the scroll container would clip. The
    // bottom margin keeps that offset and adds a step of air above the grid.
    padding: px(3),
    margin: px(-3),
    marginBottom: `calc(${spacing.xs} - ${px(3)})`,
    selectors: {
        "&::-webkit-scrollbar": {
            display: "none",
        },
    },
});

export const appsTab = style({
    ...body2Css,
    // A step under body2: one-word labels read as filters, not headings.
    fontSize: px(13),
    lineHeight: px(18),
    fontWeight: 500,
    flexShrink: 0,
    whiteSpace: "nowrap",
    paddingTop: px(5),
    paddingBottom: px(5),
    paddingLeft: px(12),
    paddingRight: px(12),
    borderRadius: borderRadius.pill,
    border: "none",
    backgroundColor: colors.gray85,
    color: colors.gray30,
    cursor: "pointer",
    transition: "background-color 150ms ease, color 150ms ease",
    selectors: {
        '&:not([aria-selected="true"]):hover': {
            backgroundColor: colors.gray80,
            color: colors.white,
        },
        '&[aria-selected="true"]': {
            backgroundColor: colors.gray60,
            color: colors.white,
        },
        "&:focus-visible": {
            outline: "none",
            boxShadow: boxShadow.focusDark,
        },
    },
});

// ── Tools section ─────────────────────────────────────────────────────────────

export const toolsGrid = style({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
    gap: spacing.xs,
    listStyle: "none",
    padding: 0,
    margin: 0,
});

export const toolListItem = style({
    display: "contents",
});

export const toolCard = style({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: spacing.xs,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.md,
    backgroundColor: "transparent",
    borderWidth: px(1),
    borderStyle: "solid",
    borderColor: colors.gray80,
    textDecoration: "none",
    color: "inherit",
    transition: "border-color 0.15s ease",
    ":hover": {
        borderColor: colors.gray60,
    },
});

export const toolCardTitle = style({
    ...body3Css,
    color: colors.gray30,
    margin: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
});

export const toolCardArrow = style({
    width: px(12),
    height: px(12),
    color: colors.gray60,
    flexShrink: 0,
});

// ── Legacy exports (used by other callers of this module) ─────────────────────

export const appList = style({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: spacing.xs,
    listStyle: "none",
    padding: 0,
    margin: 0,
});

export const item = style({
    padding: spacing.sm,
    borderWidth: px(1),
    borderStyle: "solid",
    borderColor: "rgb(100, 100, 100)",
    borderRadius: borderRadius.md,
});

export const sectionHeader = style({
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    cursor: "pointer",
    userSelect: "none",
});

export const disclosureIcon = style({
    width: px(kIconSize_px),
    height: px(kIconSize_px),
    transition: "transform 0.15s ease",
    flexShrink: 0,
});

export const disclosureIconExpanded = style({
    transform: "rotate(90deg)",
});

export const utilityList = style({
    display: "flex",
    flexWrap: "wrap",
    gap: spacing.sm,
    listStyle: "none",
    padding: 0,
    margin: 0,
});

/** The Apps grid: one card per row at phone width, so each card's description has the width. */
export const heroGridFullWidthOnPhone = style({
    "@media": {
        [kNarrowViewportQuery]: {
            selectors: {
                [kWebSelector]: {
                    gridTemplateColumns: "1fr",
                },
            },
        },
    },
});
